import { STANDARD_DURATIONS } from '@einhaus/project314-shared';
import type { TradeLogicJsonObject, TradeLogicJsonValue } from '../model';

export type PriceTargetSource =
    | 'literal'
    | 'capture'
    | 'ema'
    | 'sma'
    | 'vwap'
    | 'open'
    | 'high'
    | 'low'
    | 'close'
    | 'nearestPivot'
    | 'nearestSupport'
    | 'nearestResistance'
    | 'nearestFibonacci'
    | NamedPriceTargetSource;
export const namedPriceTargetLabels = {
    openingRangeHigh: 'Opening range high',
    openingRangeLow: 'Opening range low',
    swingHigh: 'Unbroken confirmed swing high',
    swingLow: 'Unbroken confirmed swing low',
    lowerHighsBreakout: 'Next lower-highs breakout',
    higherLowsBreakdown: 'Next higher-lows breakdown',
    rangeHigh: 'Last N completed bars high',
    rangeLow: 'Last N completed bars low',
    consolidationHigh: 'Detected consolidation high',
    consolidationLow: 'Detected consolidation low',
    previousSessionHigh: 'Previous session high',
    previousSessionLow: 'Previous session low',
    previousSessionClose: 'Previous session close',
    preMarketHigh: 'Pre-market high (stocks)',
    preMarketLow: 'Pre-market low (stocks)'
} as const;
export type NamedPriceTargetSource = keyof typeof namedPriceTargetLabels;
export const isNamedPriceTargetSource = (source: string): source is NamedPriceTargetSource => Object.hasOwn(namedPriceTargetLabels, source);
export const isRollingPriceTarget = (source: string): boolean => source === 'rangeHigh' || source === 'rangeLow';
export const priceTargetDurations = (source: PriceTargetSource): readonly string[] =>
    source.startsWith('openingRange')
        ? ['5m', '15m', '30m']
        : source.startsWith('previousSession')
          ? ['1d']
          : source.startsWith('preMarket')
            ? ['1m']
            : source === 'vwap'
              ? STANDARD_DURATIONS.filter((duration) => !['1d', '1w', '1mo'].includes(duration))
              : STANDARD_DURATIONS;

export type PriceLifetimeUnit = 'minutes' | 'hours' | 'days';
export type PriceOffsetUnit = 'absolute' | 'percent';
export type PriceCancellation =
    | { operator: '<' | '>'; price: number | null }
    | { operator: '<' | '>'; indicator: 'ema' | 'sma'; period: number | null }
    | 'advanced'
    | null;

export interface PriceExecutionDraft {
    orderType: 'limit' | 'stop';
    rangeBars: number | null;
    oncePerSource: boolean;
    breakDirection: 'touch' | 'above' | 'below';
    priceIncrement: number | null;
    source: PriceTargetSource;
    captureKey: string | null;
    duration: string;
    literalPrice: number | null;
    period: number | null;
    offsetBars: number | null;
    offsetUnit: PriceOffsetUnit | null;
    offsetAmount: number | null;
    targetPolicy: 'freeze_at_arm' | 'refresh_on_reference_close';
    lifetimeUnit: PriceLifetimeUnit;
    lifetimeValue: number | null;
    cancellation: PriceCancellation;
}

const isObject = (value: unknown): value is TradeLogicJsonObject => typeof value === 'object' && value !== null && !Array.isArray(value);
const hasOnlyKeys = (value: TradeLogicJsonObject, keys: readonly string[]): boolean =>
    Object.keys(value).every((key) => keys.includes(key));
const isPriceTargetSource = (value: string): value is Exclude<PriceTargetSource, 'literal'> =>
    ['ema', 'sma', 'vwap', 'open', 'high', 'low', 'close'].includes(value);
const structuralFeatureBySource = {
    nearestPivot: 'entity.keyLevelMetrics.proximity.nearestPivot.value',
    nearestSupport: 'entity.keyLevelMetrics.proximity.nearestSupport.value',
    nearestResistance: 'entity.keyLevelMetrics.proximity.nearestResistance.value',
    nearestFibonacci: 'entity.keyLevelMetrics.proximity.nearestFibonacci.value'
} as const;
type StructuralPriceTargetSource = keyof typeof structuralFeatureBySource;
export const isStructuralPriceTargetSource = (source: PriceTargetSource): source is StructuralPriceTargetSource =>
    source in structuralFeatureBySource;
const structuralSourceByFeature = new Map<string, StructuralPriceTargetSource>(
    Object.entries(structuralFeatureBySource).map(([source, feature]) => [feature, source as StructuralPriceTargetSource])
);

export const defaultPriceExecutionDraft = (): PriceExecutionDraft => ({
    orderType: 'limit',
    rangeBars: 20,
    oncePerSource: false,
    breakDirection: 'touch',
    priceIncrement: null,
    source: 'ema',
    captureKey: null,
    duration: '60m',
    literalPrice: null,
    period: 8,
    offsetBars: 0,
    offsetUnit: null,
    offsetAmount: null,
    targetPolicy: 'freeze_at_arm',
    lifetimeUnit: 'hours',
    lifetimeValue: 2,
    cancellation: null
});

const readCancellation = (value: TradeLogicJsonValue | undefined, duration: string): PriceCancellation => {
    if (value == null) return null;
    if (!isObject(value) || !hasOnlyKeys(value, ['kind', 'left', 'operator', 'right']) || value.kind !== 'comparison') {
        return 'advanced';
    }
    const left = value.left;
    const right = value.right;
    if (
        !isObject(left) ||
        !hasOnlyKeys(left, ['kind', 'ref']) ||
        left.kind !== 'contextFeature' ||
        !isObject(left.ref) ||
        !hasOnlyKeys(left.ref, ['kind', 'featureKey', 'duration']) ||
        left.ref.kind !== 'contextFeature' ||
        left.ref.featureKey !== 'price.ohlcv.close' ||
        left.ref.duration !== duration ||
        (value.operator !== '<' && value.operator !== '>')
    ) {
        return 'advanced';
    }
    if (isObject(right) && hasOnlyKeys(right, ['kind', 'value']) && right.kind === 'literal' && typeof right.value === 'number') {
        return { operator: value.operator, price: right.value };
    }
    if (
        isObject(right) &&
        hasOnlyKeys(right, ['kind', 'ref']) &&
        right.kind === 'contextFeature' &&
        isObject(right.ref) &&
        hasOnlyKeys(right.ref, ['kind', 'featureKey', 'duration', 'selectors']) &&
        right.ref.kind === 'contextFeature' &&
        right.ref.duration === duration &&
        (right.ref.featureKey === 'price.indicator.ema' || right.ref.featureKey === 'price.indicator.sma') &&
        isObject(right.ref.selectors) &&
        hasOnlyKeys(right.ref.selectors, ['period']) &&
        typeof right.ref.selectors.period === 'number' &&
        Number.isInteger(right.ref.selectors.period) &&
        right.ref.selectors.period >= 1 &&
        right.ref.selectors.period <= 1000
    ) {
        return {
            operator: value.operator,
            indicator: right.ref.featureKey === 'price.indicator.ema' ? 'ema' : 'sma',
            period: right.ref.selectors.period
        };
    }
    return 'advanced';
};

/** Unknown advanced expressions are kept in the saved node, never simplified on load. */
export const readPriceExecutionDraft = (value: TradeLogicJsonValue | undefined): PriceExecutionDraft | null => {
    if (
        !isObject(value) ||
        !hasOnlyKeys(value, ['orderType', 'target', 'referenceDuration', 'targetPolicy', 'lifetime', 'invalidateWhen', 'oncePerSource']) ||
        !isObject(value.target) ||
        !isObject(value.lifetime) ||
        !hasOnlyKeys(value.lifetime, ['unit', 'value'])
    )
        return null;
    let target = value.target;
    let breakDirection: PriceExecutionDraft['breakDirection'] = 'touch';
    let priceIncrement: number | null = null;
    if (target.kind === 'formula') {
        const unwrapped = readBreakTarget(target);
        if (!unwrapped) return null;
        target = unwrapped.target;
        breakDirection = unwrapped.direction;
        priceIncrement = unwrapped.increment;
    }
    if (value.oncePerSource !== undefined && typeof value.oncePerSource !== 'boolean') return null;
    let rangeBars: number | null = 20;
    const lifetime = value.lifetime;
    if (
        (value.orderType !== 'limit' && value.orderType !== 'stop') ||
        (value.targetPolicy !== 'freeze_at_arm' && value.targetPolicy !== 'refresh_on_reference_close') ||
        typeof value.referenceDuration !== 'string' ||
        !STANDARD_DURATIONS.some((duration) => duration === value.referenceDuration) ||
        (lifetime.unit !== 'minutes' && lifetime.unit !== 'hours' && lifetime.unit !== 'days') ||
        typeof lifetime.value !== 'number' ||
        !Number.isSafeInteger(lifetime.value) ||
        lifetime.value <= 0
    ) {
        return null;
    }

    let source: PriceTargetSource;
    let captureKey: string | null = null;
    let literalPrice: number | null = null;
    let period: number | null = null;
    let offsetBars: number | null = 0;
    let offsetUnit: PriceOffsetUnit | null = null;
    let offsetAmount: number | null = null;
    if (target.kind === 'priceOffset') {
        if (
            !hasOnlyKeys(target, ['kind', 'base', 'amount', 'unit']) ||
            (target.unit !== 'absolute' && target.unit !== 'percent') ||
            typeof target.amount !== 'number' ||
            !Number.isFinite(target.amount) ||
            !isObject(target.base)
        )
            return null;
        offsetUnit = target.unit;
        offsetAmount = target.amount;
        target = target.base;
    }
    if (
        target.kind === 'capture' &&
        hasOnlyKeys(target, ['kind', 'captureKey']) &&
        typeof target.captureKey === 'string' &&
        target.captureKey.trim()
    ) {
        source = 'capture';
        captureKey = target.captureKey;
    } else if (target.kind === 'literal' && hasOnlyKeys(target, ['kind', 'value']) && typeof target.value === 'number') {
        source = 'literal';
        literalPrice = target.value;
    } else if (target.kind === 'contextFeature' && hasOnlyKeys(target, ['kind', 'ref']) && isObject(target.ref)) {
        const ref = target.ref;
        const structuralSource = typeof ref.featureKey === 'string' ? structuralSourceByFeature.get(ref.featureKey) : undefined;
        if (typeof ref.featureKey === 'string' && ref.featureKey.startsWith('price.reference.')) {
            const named = ref.featureKey.slice('price.reference.'.length);
            if (
                !isNamedPriceTargetSource(named) ||
                ref.kind !== 'contextFeature' ||
                ref.duration !== value.referenceDuration ||
                !hasOnlyKeys(ref, ['kind', 'featureKey', 'duration', 'selectors']) ||
                value.targetPolicy !== 'freeze_at_arm' ||
                !priceTargetDurations(named).includes(value.referenceDuration)
            )
                return null;
            if (isRollingPriceTarget(named)) {
                if (
                    !isObject(ref.selectors) ||
                    !hasOnlyKeys(ref.selectors, ['bars']) ||
                    typeof ref.selectors.bars !== 'number' ||
                    !Number.isInteger(ref.selectors.bars) ||
                    ref.selectors.bars < 1 ||
                    ref.selectors.bars > 200
                )
                    return null;
                rangeBars = ref.selectors.bars;
            } else if (ref.selectors !== undefined) return null;
            source = named;
        } else if (structuralSource) {
            if (ref.kind !== 'contextFeature' || !hasOnlyKeys(ref, ['kind', 'featureKey']) || value.targetPolicy !== 'freeze_at_arm') {
                return null;
            }
            source = structuralSource;
        } else {
            if (ref.duration !== value.referenceDuration) return null;
            if (
                ref.kind !== 'contextFeature' ||
                !hasOnlyKeys(ref, ['kind', 'featureKey', 'duration', 'selectors', 'offsetBars']) ||
                typeof ref.featureKey !== 'string' ||
                !ref.featureKey.startsWith('price.')
            )
                return null;
            const key = ref.featureKey.replace(/^price\.(indicator|ohlcv)\./, '');
            if (!isPriceTargetSource(key)) return null;
            source = key;
            if (source === 'ema' || source === 'sma') {
                if (
                    ref.featureKey !== `price.indicator.${source}` ||
                    !isObject(ref.selectors) ||
                    !hasOnlyKeys(ref.selectors, ['period']) ||
                    typeof ref.selectors.period !== 'number' ||
                    ref.offsetBars != null
                )
                    return null;
                period = ref.selectors.period;
                offsetBars = 0;
            } else if (source === 'vwap') {
                if (
                    ref.featureKey !== 'price.indicator.vwap' ||
                    (ref.selectors != null && (!isObject(ref.selectors) || Object.keys(ref.selectors).length > 0)) ||
                    (ref.offsetBars != null && ref.offsetBars !== 0) ||
                    ['1d', '1w', '1mo'].includes(value.referenceDuration)
                )
                    return null;
                offsetBars = 0;
            } else {
                if (
                    ref.featureKey !== `price.ohlcv.${source}` ||
                    ref.selectors != null ||
                    (ref.offsetBars != null && typeof ref.offsetBars !== 'number')
                )
                    return null;
                offsetBars = typeof ref.offsetBars === 'number' ? ref.offsetBars : 0;
            }
        }
    } else {
        return null;
    }
    return {
        orderType: value.orderType,
        source,
        captureKey,
        duration: value.referenceDuration,
        literalPrice,
        rangeBars,
        oncePerSource: value.oncePerSource === true,
        breakDirection,
        priceIncrement,
        period,
        offsetBars,
        offsetUnit,
        offsetAmount,
        targetPolicy: value.targetPolicy,
        lifetimeUnit: lifetime.unit,
        lifetimeValue: lifetime.value,
        cancellation: readCancellation(value.invalidateWhen, value.referenceDuration)
    };
};

export const buildPriceExecution = (draft: PriceExecutionDraft, saved?: TradeLogicJsonValue): TradeLogicJsonObject => {
    if (!priceTargetDurations(draft.source).includes(draft.duration))
        throw new Error(
            draft.source === 'vwap'
                ? 'VWAP requires an intraday reference timeframe.'
                : 'Select a supported reference duration for this source.'
        );
    if (draft.lifetimeValue === null || !Number.isSafeInteger(draft.lifetimeValue) || draft.lifetimeValue <= 0) {
        throw new Error('Lifetime must be a positive whole number.');
    }
    const lifetimeSeconds = draft.lifetimeValue * (draft.lifetimeUnit === 'days' ? 86400 : draft.lifetimeUnit === 'hours' ? 3600 : 60);
    if (!Number.isSafeInteger(lifetimeSeconds)) throw new Error('Lifetime exceeds the supported timestamp range.');
    let target: TradeLogicJsonObject;
    if (draft.source === 'capture') {
        if (!draft.captureKey?.trim()) throw new Error('Choose a saved sequence capture.');
        if (draft.targetPolicy !== 'freeze_at_arm') throw new Error('Captured targets must freeze when the sequence completes.');
        target = { kind: 'capture', captureKey: draft.captureKey };
    } else if (isNamedPriceTargetSource(draft.source)) {
        if (draft.targetPolicy !== 'freeze_at_arm') throw new Error('Named boundaries must freeze when the setup completes.');
        if (
            isRollingPriceTarget(draft.source) &&
            (draft.rangeBars === null || !Number.isInteger(draft.rangeBars) || draft.rangeBars < 1 || draft.rangeBars > 200)
        )
            throw new Error('Range length must be 1–200 completed bars.');
        target = {
            kind: 'contextFeature',
            ref: {
                kind: 'contextFeature',
                featureKey: `price.reference.${draft.source}`,
                duration: draft.duration,
                ...(isRollingPriceTarget(draft.source) ? { selectors: { bars: draft.rangeBars } } : {})
            }
        };
    } else if (draft.source === 'literal') {
        if (draft.literalPrice === null || !Number.isFinite(draft.literalPrice) || draft.literalPrice <= 0) {
            throw new Error('Enter a positive target price.');
        }
        target = { kind: 'literal', value: draft.literalPrice };
    } else {
        const isMovingAverage = draft.source === 'ema' || draft.source === 'sma';
        const isIndicator = isMovingAverage || draft.source === 'vwap';
        const isStructural = isStructuralPriceTargetSource(draft.source);
        if (isMovingAverage && (draft.period === null || !Number.isInteger(draft.period) || draft.period < 1 || draft.period > 1000)) {
            throw new Error('Moving-average period must be a whole number from 1 to 1000.');
        }
        if (draft.source === 'vwap' && ['1d', '1w', '1mo'].includes(draft.duration)) {
            throw new Error('VWAP requires an intraday reference timeframe.');
        }
        if (
            !isIndicator &&
            !isStructural &&
            (draft.offsetBars === null || !Number.isInteger(draft.offsetBars) || draft.offsetBars < 0 || draft.offsetBars > 6)
        ) {
            throw new Error('Completed bars back must be a whole number from 0 to 6.');
        }
        if (isStructural) {
            if (draft.targetPolicy !== 'freeze_at_arm') throw new Error('Structural targets must be frozen when the setup completes.');
            target = {
                kind: 'contextFeature',
                ref: {
                    kind: 'contextFeature',
                    featureKey: structuralFeatureBySource[draft.source as StructuralPriceTargetSource]
                }
            };
        } else {
            target = {
                kind: 'contextFeature',
                ref: {
                    kind: 'contextFeature',
                    featureKey: `price.${isIndicator ? 'indicator' : 'ohlcv'}.${draft.source}`,
                    duration: draft.duration,
                    ...(isMovingAverage ? { selectors: { period: draft.period } } : isIndicator ? {} : { offsetBars: draft.offsetBars })
                }
            };
        }
    }
    if (draft.offsetUnit !== null) {
        if (draft.offsetAmount === null || !Number.isFinite(draft.offsetAmount)) {
            throw new Error('Enter a finite price offset.');
        }
        if (draft.offsetUnit === 'percent' && draft.offsetAmount <= -100) {
            throw new Error('Percentage offset must be greater than -100%.');
        }
        if (draft.source === 'literal') {
            const basePrice = draft.literalPrice;
            if (basePrice === null) throw new Error('Enter a positive target price.');
            const adjusted = draft.offsetUnit === 'absolute' ? basePrice + draft.offsetAmount : basePrice * (1 + draft.offsetAmount / 100);
            if (!Number.isFinite(adjusted) || adjusted <= 0) throw new Error('Offset target price must remain positive.');
        }
        target = { kind: 'priceOffset', base: target, amount: draft.offsetAmount, unit: draft.offsetUnit };
    }
    if (draft.oncePerSource && !isNamedPriceTargetSource(draft.source) && !isStructuralPriceTargetSource(draft.source)) {
        throw new Error('One attempt per source requires a named boundary or structural level.');
    }
    if (draft.breakDirection !== 'touch') {
        if (draft.orderType !== 'stop') throw new Error('A breakout requires stop behavior.');
        if (draft.priceIncrement === null || !Number.isFinite(draft.priceIncrement) || draft.priceIncrement <= 0)
            throw new Error('Enter the instrument’s valid positive price increment.');
        target = buildBreakTarget(target, draft.breakDirection, draft.priceIncrement);
    }
    let invalidateWhen: TradeLogicJsonValue | undefined;
    if (draft.cancellation === 'advanced') {
        invalidateWhen = isObject(saved) ? saved.invalidateWhen : undefined;
        if (invalidateWhen == null) throw new Error('The advanced cancellation condition is unavailable.');
    } else if (draft.cancellation) {
        let right: TradeLogicJsonObject;
        if ('price' in draft.cancellation) {
            if (draft.cancellation.price === null || !Number.isFinite(draft.cancellation.price) || draft.cancellation.price <= 0) {
                throw new Error('Enter a positive cancellation close price.');
            }
            right = { kind: 'literal', value: draft.cancellation.price };
        } else {
            if (
                draft.cancellation.period === null ||
                !Number.isInteger(draft.cancellation.period) ||
                draft.cancellation.period < 1 ||
                draft.cancellation.period > 1000
            ) {
                throw new Error('Cancellation moving-average period must be a whole number from 1 to 1000.');
            }
            right = {
                kind: 'contextFeature',
                ref: {
                    kind: 'contextFeature',
                    featureKey: `price.indicator.${draft.cancellation.indicator}`,
                    duration: draft.duration,
                    selectors: { period: draft.cancellation.period }
                }
            };
        }
        invalidateWhen = {
            kind: 'comparison',
            left: { kind: 'contextFeature', ref: { kind: 'contextFeature', featureKey: 'price.ohlcv.close', duration: draft.duration } },
            operator: draft.cancellation.operator,
            right
        };
    }
    return {
        orderType: draft.orderType,
        target,
        referenceDuration: draft.duration,
        targetPolicy: draft.targetPolicy,
        lifetime: { unit: draft.lifetimeUnit, value: draft.lifetimeValue },
        ...(invalidateWhen != null ? { invalidateWhen } : {}),
        ...(draft.oncePerSource ? { oncePerSource: true } : {})
    };
};

export const withPriceExecution = (parameters: TradeLogicJsonObject, execution: TradeLogicJsonValue | null): TradeLogicJsonObject => {
    const next = { ...parameters };
    if (execution === null) {
        delete next.priceExecution;
    } else {
        delete next.price;
        delete next.limitPrice;
        delete next.stopPrice;
        next.priceExecution = execution;
    }
    return next;
};

const buildBreakTarget = (target: TradeLogicJsonObject, direction: 'above' | 'below', increment: number): TradeLogicJsonObject => ({
    kind: 'formula',
    operator: 'multiply',
    operands: [
        {
            kind: 'formula',
            operator: 'add',
            operands: [
                {
                    kind: 'rounded',
                    value: {
                        kind: 'rounded',
                        value: { kind: 'formula', operator: 'divide', operands: [target, { kind: 'literal', value: increment }] },
                        precision: 8,
                        mode: 'nearest'
                    },
                    precision: 0,
                    mode: direction === 'above' ? 'down' : 'up'
                },
                { kind: 'literal', value: direction === 'above' ? 1 : -1 }
            ]
        },
        { kind: 'literal', value: increment }
    ]
});

const readBreakTarget = (
    value: TradeLogicJsonObject
): { target: TradeLogicJsonObject; direction: 'above' | 'below'; increment: number } | null => {
    if (
        !hasOnlyKeys(value, ['kind', 'operator', 'operands']) ||
        value.operator !== 'multiply' ||
        !Array.isArray(value.operands) ||
        value.operands.length !== 2
    )
        return null;
    const [sum, tick] = value.operands;
    if (
        !isObject(tick) ||
        !hasOnlyKeys(tick, ['kind', 'value']) ||
        tick.kind !== 'literal' ||
        typeof tick.value !== 'number' ||
        !Number.isFinite(tick.value) ||
        tick.value <= 0 ||
        !isObject(sum) ||
        !hasOnlyKeys(sum, ['kind', 'operator', 'operands']) ||
        sum.kind !== 'formula' ||
        sum.operator !== 'add' ||
        !Array.isArray(sum.operands) ||
        sum.operands.length !== 2
    )
        return null;
    const [rounded, step] = sum.operands;
    if (
        !isObject(step) ||
        !hasOnlyKeys(step, ['kind', 'value']) ||
        step.kind !== 'literal' ||
        (step.value !== 1 && step.value !== -1) ||
        !isObject(rounded) ||
        !hasOnlyKeys(rounded, ['kind', 'value', 'precision', 'mode']) ||
        rounded.kind !== 'rounded' ||
        rounded.precision !== 0 ||
        rounded.mode !== (step.value === 1 ? 'down' : 'up') ||
        !isObject(rounded.value) ||
        !hasOnlyKeys(rounded.value, ['kind', 'value', 'precision', 'mode']) ||
        rounded.value.kind !== 'rounded' ||
        rounded.value.precision !== 8 ||
        rounded.value.mode !== 'nearest'
    )
        return null;
    const quotient = rounded.value.value;
    if (
        !isObject(quotient) ||
        !hasOnlyKeys(quotient, ['kind', 'operator', 'operands']) ||
        quotient.kind !== 'formula' ||
        quotient.operator !== 'divide' ||
        !Array.isArray(quotient.operands) ||
        quotient.operands.length !== 2
    )
        return null;
    const [target, divisor] = quotient.operands;
    if (
        !isObject(target) ||
        !isObject(divisor) ||
        !hasOnlyKeys(divisor, ['kind', 'value']) ||
        divisor.kind !== 'literal' ||
        divisor.value !== tick.value
    )
        return null;
    return { target, direction: step.value === 1 ? 'above' : 'below', increment: tick.value };
};
